/**
 * ============================================================================
 * USE VARIABLE REFERENCE MANAGER
 * ============================================================================
 *
 * Hook for managing inline `{{ key.name }}` variable references inside
 * a contentEditable element. Responsibilities:
 *
 *  1. Detect `{{` trigger and provide autocomplete positioning/state
 *  2. Apply visual decoration (colored chips) on blur / when not focused
 *  3. Strip decoration when saving or before comparing content
 *  4. Insert a reference token at the current cursor position
 *
 * Works alongside the existing useTextHighlightManager hook.
 *
 * @module editor/hooks/useVariableReferenceManager
 */

import { useCallback, useState, useMemo, useRef } from 'react';
import {
  parseVariableReferences,
  resolveAllReferences,
  decorateReferencesInHTML,
  stripReferenceDecoration,
  cleanVariableTokens,
  detectVariableRefTrigger,
  buildSuggestionItems,
  type ResolvedReference,
  type VariableRefSuggestionItem,
} from '@/lib/utils/variableReferences';
import type { TypeItem } from '@/lib/stores/typesStore';
import type { ObjectItem } from '@/lib/stores/objects/types';

// ============================================================================
// TYPES
// ============================================================================

export interface VariableRefAutocompleteState {
  /** Whether the autocomplete dropdown should be visible */
  isOpen: boolean;
  /** Position (viewport-relative) for the dropdown */
  position: { x: number; y: number };
  /** Current partial query the user typed after `{{` */
  query: string;
}

export interface UseVariableReferenceManagerOptions {
  /** Available TypeItems for resolution */
  types: TypeItem[];
  /** Available ObjectItems for resolution */
  objects: ObjectItem[];
}

// ============================================================================
// HOOK
// ============================================================================

export function useVariableReferenceManager(options: UseVariableReferenceManagerOptions) {
  const { types, objects } = options;

  // Autocomplete state
  const [autocomplete, setAutocomplete] = useState<VariableRefAutocompleteState>({
    isOpen: false,
    position: { x: 0, y: 0 },
    query: '',
  });

  // Track the range where the trigger started so we can replace it on selection
  const triggerRangeRef = useRef<{ node: Node; offset: number } | null>(null);

  // ──────────────────────────────────────────────
  // Suggestion items (filtered by current query)
  // ──────────────────────────────────────────────
  const suggestions = useMemo<VariableRefSuggestionItem[]>(() => {
    if (!autocomplete.isOpen) return [];
    return buildSuggestionItems(types, objects, autocomplete.query);
  }, [autocomplete.isOpen, autocomplete.query, types, objects]);

  // ──────────────────────────────────────────────
  // Detect `{{` trigger on input
  // ──────────────────────────────────────────────
  const handleInputForVarRef = useCallback(
    (element: HTMLDivElement) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      if (!range.collapsed) return;

      // Get text before cursor
      const cursorNode = range.startContainer;
      const cursorOffset = range.startOffset;

      // Only work with text nodes
      if (cursorNode.nodeType !== Node.TEXT_NODE) {
        // Close autocomplete if cursor is not in a text node
        if (autocomplete.isOpen) {
          setAutocomplete((prev) => ({ ...prev, isOpen: false }));
        }
        return;
      }

      const textContent = cursorNode.textContent || '';
      const textBeforeCursor = textContent.substring(0, cursorOffset);

      // Check for closing `}}` — if found, close autocomplete
      if (textBeforeCursor.endsWith('}}')) {
        if (autocomplete.isOpen) {
          setAutocomplete((prev) => ({ ...prev, isOpen: false }));
          triggerRangeRef.current = null;
        }
        return;
      }

      const trigger = detectVariableRefTrigger(textBeforeCursor);

      if (trigger.isTriggered) {
        // Calculate position for the dropdown
        const rect = range.getBoundingClientRect();
        const containerRect = element.getBoundingClientRect();

        // Position below the cursor
        setAutocomplete({
          isOpen: true,
          position: {
            x: rect.left - containerRect.left,
            y: rect.bottom - containerRect.top + 4,
          },
          query: trigger.query,
        });

        // Store trigger start position (after `{{`)
        // Find the `{{` in the text before cursor
        const triggerIdx = textBeforeCursor.lastIndexOf('{{');
        if (triggerIdx >= 0) {
          triggerRangeRef.current = {
            node: cursorNode,
            offset: triggerIdx,
          };
        }
      } else {
        if (autocomplete.isOpen) {
          setAutocomplete((prev) => ({ ...prev, isOpen: false }));
          triggerRangeRef.current = null;
        }
      }
    },
    [autocomplete.isOpen]
  );

  // ──────────────────────────────────────────────
  // Close the autocomplete dropdown
  // ──────────────────────────────────────────────
  const closeAutocomplete = useCallback(() => {
    setAutocomplete({ isOpen: false, position: { x: 0, y: 0 }, query: '' });
    triggerRangeRef.current = null;
  }, []);

  // ──────────────────────────────────────────────
  // Insert a selected reference at the cursor
  // ──────────────────────────────────────────────
  const insertReference = useCallback(
    (refString: string, editableElement: HTMLDivElement) => {
      const token = `{{ ${refString} }}`;
      const trigger = triggerRangeRef.current;

      if (trigger && trigger.node.parentNode) {
        const selection = window.getSelection();
        if (!selection) return;

        const textContent = trigger.node.textContent || '';
        const cursorOffset = selection.focusOffset;

        // Replace from `{{...` (trigger start) to current cursor position
        const before = textContent.substring(0, trigger.offset);
        const after = textContent.substring(cursorOffset);

        // Set the text content with the token inserted
        trigger.node.textContent = before + token + after;
      } else {
        // Fallback: insert at current selection
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          const textNode = document.createTextNode(token);
          range.insertNode(textNode);
        }
      }

      // Clean up state
      closeAutocomplete();

      // Immediately decorate so the token becomes a styled chip
      const currentHTML = editableElement.innerHTML;
      const clean = stripReferenceDecoration(currentHTML);
      const resolved = resolveAllReferences(clean, types, objects);
      const decorated = decorateReferencesInHTML(clean, resolved);
      if (decorated !== currentHTML) {
        editableElement.innerHTML = decorated;
      }

      // Ensure the element has focus (user may have clicked the dropdown)
      editableElement.focus();

      // Place cursor after the newly inserted var-ref span
      try {
        const selection = window.getSelection();
        if (selection) {
          // Find all var-ref spans and position cursor after the last one
          // (the one we just inserted)
          const refSpans = editableElement.querySelectorAll('.var-ref');
          if (refSpans.length > 0) {
            const lastSpan = refSpans[refSpans.length - 1];
            const range = document.createRange();

            // Insert a zero-width space after the span for cursor positioning
            let nextSibling = lastSpan.nextSibling;
            if (!nextSibling || nextSibling.nodeType !== Node.TEXT_NODE) {
              const spacer = document.createTextNode('\u200B');
              lastSpan.parentNode?.insertBefore(spacer, lastSpan.nextSibling);
              nextSibling = spacer;
            }

            // Position cursor right after the zero-width space / at start of the next text node
            const offset = (nextSibling.nodeType === Node.TEXT_NODE && nextSibling.textContent)
              ? Math.min(1, nextSibling.textContent.length)
              : 0;
            range.setStart(nextSibling, offset);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      } catch {
        // If cursor positioning fails, that's okay
      }

      // Note: We do NOT dispatch an input event here.
      // The caller (TextBlock onSelect) handles onContentChange explicitly
      // to avoid double-saves and to ensure decoration markup is stripped.
    },
    [closeAutocomplete, types, objects]
  );

  // ──────────────────────────────────────────────
  // Decorate references in HTML (for display)
  // ──────────────────────────────────────────────
  const decorateHTML = useCallback(
    (html: string): string => {
      // First strip any existing decorations to avoid double-wrapping
      let clean = stripReferenceDecoration(html);
      // Also clean any corrupted tokens (e.g. highlight spans inside {{ }})
      clean = cleanVariableTokens(clean);
      const resolved = resolveAllReferences(clean, types, objects);
      return decorateReferencesInHTML(clean, resolved);
    },
    [types, objects]
  );

  // ──────────────────────────────────────────────
  // Apply decoration to a contentEditable element
  // ──────────────────────────────────────────────
  const applyDecoration = useCallback(
    (element: HTMLDivElement): string => {
      const currentHTML = element.innerHTML;
      const decorated = decorateHTML(currentHTML);
      if (decorated !== currentHTML) {
        element.innerHTML = decorated;
      }
      return decorated;
    },
    [decorateHTML]
  );

  // ──────────────────────────────────────────────
  // Strip decoration from a contentEditable element
  // ──────────────────────────────────────────────
  const stripDecoration = useCallback((element: HTMLDivElement): string => {
    const currentHTML = element.innerHTML;
    const stripped = stripReferenceDecoration(currentHTML);
    if (stripped !== currentHTML) {
      element.innerHTML = stripped;
    }
    return stripped;
  }, []);

  // ──────────────────────────────────────────────
  // Resolve all references in text (for metadata)
  // ──────────────────────────────────────────────
  const resolveRefs = useCallback(
    (text: string): ResolvedReference[] => {
      return resolveAllReferences(text, types, objects);
    },
    [types, objects]
  );

  // ──────────────────────────────────────────────
  // Check if content has any variable references
  // ──────────────────────────────────────────────
  const hasReferences = useCallback((text: string): boolean => {
    return parseVariableReferences(text).length > 0;
  }, []);

  return {
    // Autocomplete state
    autocomplete,
    suggestions,
    closeAutocomplete,

    // Input handling
    handleInputForVarRef,
    insertReference,

    // Decoration
    decorateHTML,
    applyDecoration,
    stripDecoration,
    stripReferenceDecoration,

    // Resolution
    resolveRefs,
    hasReferences,
  };
}
